import { FC, useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { AddPhotoAlternateRounded, RefreshRounded } from "@mui/icons-material";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";

import FormLayout from "../../FormLayout";
import imageSliderController from "../../../../Api/imageSliderController";
import { ISlide } from "../../../../interfaces/ISlide";
import SliderItem from "./components/SliderItem";
import NewSlideComponent from "./components/NewSlideComponent";

interface ImageSliderFormProps {
    embedded?: boolean;
}

const ImageSliderForm: FC<ImageSliderFormProps> = ({ embedded = false }) => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [images, setImages] = useState<ISlide[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const getSlides = useCallback(async () => {
        setLoading(true);
        setError('');
        const res = await imageSliderController.getAllImagesAdmin();
        if (!res.ok || !res.images) {
            setImages([]);
            setError(res.message || 'Nie udało się pobrać slajdów.');
        } else {
            setImages(res.images);
        }
        setLoading(false);
    }, []);

    useEffect(() => { getSlides(); }, [getSlides]);

    const removeSlide = (_id: string) => setImages((current) => current.filter((image) => image._id !== _id));

    const handleSetImages = async (updatedItems: ISlide[]) => {
        const previous = images;
        setImages(updatedItems);
        const res = await imageSliderController.updateImagesOrder(updatedItems.map((item) => item._id));
        if (!res.ok) {
            setImages(previous);
            setError(res.message);
        }
    };

    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = images.findIndex((image) => image._id === active.id);
        const newIndex = images.findIndex((image) => image._id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) handleSetImages(arrayMove(images, oldIndex, newIndex));
    };

    const content = <Box sx={{ width: '100%' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} gap={2} mb={3}>
            <Box>
                <Typography variant="h5" fontWeight={900}>Slajdy strony głównej</Typography>
                <Typography color="text.secondary" mt={0.5}>Dodawaj slajdy, ustawiaj terminy publikacji i przeciągaj je, aby zmienić kolejność.</Typography>
            </Box>
            <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip label={`${images.length} slajdów`} sx={{ alignSelf: 'center', fontWeight: 800 }} />
                <Button variant="outlined" startIcon={<RefreshRounded />} onClick={getSlides}>Odśwież</Button>
                <Button variant="contained" startIcon={<AddPhotoAlternateRounded />} onClick={() => setShowAddForm((current) => !current)}>
                    {showAddForm ? 'Anuluj' : 'Dodaj slajd'}
                </Button>
            </Stack>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {showAddForm && <NewSlideComponent onHideForm={() => setShowAddForm(false)} onRefreshSlider={getSlides} />}

        {loading ? <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box> :
            images.length === 0 ? <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 3, bgcolor: '#f8fafc' }}>
                <Typography fontWeight={850}>Brak slajdów</Typography>
                <Typography color="text.secondary" mt={0.5}>Dodaj pierwszy slajd przyciskiem powyżej.</Typography>
            </Paper> :
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={images.map((image) => image._id)} strategy={verticalListSortingStrategy}>
                        <Stack gap={2}>
                            {images.map((image) => <SliderItem key={image._id} _id={image._id} image={image.image} link={image.link} name={image.name} publish={image.publishedAt} expire={image.expiresAt} remove={image.deletedAt} onRemoveSlide={removeSlide} />)}
                        </Stack>
                    </SortableContext>
                </DndContext>}
    </Box>;

    if (embedded) return content;
    return <FormLayout title="Zarządzanie galerią" maxWidth="xl">{content}</FormLayout>;
};

export default ImageSliderForm;
